import { useState, useCallback, useMemo } from 'react';
import {
  generateId,
  useSelectedItem,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';

interface UseQuestionGroupParams {
  section: PolicyFormSection;
  subsection: PolicyFormSubSection;
  group: PolicyFormQuestionGroup;
}

export function useQuestionGroup({ section, subsection, group }: UseQuestionGroupParams) {
  const [showSettings, setShowSettings] = useState(false);
  const selectedItem = useSelectedItem();
  const { setSelectedItem, updateGroup, addQuestion } = getFormBuilderActions();

  const isSelected = selectedItem?.type === 'group' && selectedItem.id === group.id;
  const hasCondition = !!group.conditional?.visible;

  const handleClick = useCallback(() => {
    setSelectedItem({
      type: 'group',
      id: group.id,
      sectionId: section.id,
      subsectionId: subsection.id,
    });
  }, [setSelectedItem, group.id, section.id, subsection.id]);

  const handleAddQuestion = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      const questionRef = generateId('question');
      const newQuestion: PolicyFormQuestion = {
        id: generateId('q'),
        questionRef,
        title: 'New Question',
        type: 'text',
        required: false,
      };
      addQuestion(section.id, subsection.id, group.id, newQuestion);
    },
    [addQuestion, section.id, subsection.id, group.id],
  );

  const handleSaveSettings = useCallback(
    (updates: Partial<PolicyFormQuestionGroup>) => {
      updateGroup(section.id, subsection.id, group.id, updates);
    },
    [updateGroup, section.id, subsection.id, group.id],
  );

  const handleOpenSettings = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setShowSettings(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const formatClasses = useMemo(() => {
    switch (group.format) {
      case 'grid':
        return 'grid grid-cols-2 gap-4';
      case 'inline':
        return 'flex flex-wrap gap-4';
      default:
        return 'space-y-4';
    }
  }, [group.format]);

  return {
    showSettings,
    isSelected,
    hasCondition,
    formatClasses,
    handleClick,
    handleAddQuestion,
    handleSaveSettings,
    handleOpenSettings,
    handleCloseSettings,
  };
}
